/* ============================================================
   EMMERA — DIAGNOSTIC v2
   Multi-chemin : peau / moment / complet
   Claude Vision optionnel, localStorage, add to cart
   ============================================================ */
'use strict';

class EmmeraDiagnosticV2 {

  constructor() {
    this.path    = null;
    this.answers = {};
    this.photo   = null;
    this.loadingInterval = null;
    this.init();
  }

  init() {
    this.loadFromPreviousDiag();
    this.bindPathSelection();
    this.bindCardSelection();
    this.bindPhotoUpload();
    this.bindNavigation();
  }

  /* ── Profil sauvegardé ── */
  loadFromPreviousDiag() {
    try {
      const saved = localStorage.getItem('emmera_diagnostic_v2');
      if (!saved) return;
      const data = JSON.parse(saved);
      const banner = document.getElementById('emmDiagReturning');
      if (!banner) return;
      banner.style.display = 'block';
      const txt = banner.querySelector('.emm-returning-text');
      if (txt) txt.textContent =
        `On a gardé ton profil du ${data.date}. Tu veux le mettre à jour ou voir tes recommandations ?`;
      banner.querySelector('[data-action="see-results"]')?.addEventListener('click', () => {
        banner.style.display = 'none';
        this.showResults(data.result);
      });
      banner.querySelector('[data-action="restart"]')?.addEventListener('click', () => {
        banner.style.display = 'none';
        localStorage.removeItem('emmera_diagnostic_v2');
      });
    } catch(e) {}
  }

  /* ── Choix du chemin initial ── */
  bindPathSelection() {
    document.querySelectorAll('[data-path]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.path = btn.dataset.path;
        this.hideStep('emmDiagStep0');
        const id = 'emmDiagPath' + this.path.charAt(0).toUpperCase() + this.path.slice(1);
        this.showStep(id);
        this.showQuestion(this.getFlow()[0]);
        this.scrollTo('emmDiagnostic');
      });
    });
  }

  /* ── Sélection de cartes ── */
  bindCardSelection() {
    document.querySelectorAll('.emm-diag-card').forEach(card => {
      card.addEventListener('click', () => {
        const group = card.dataset.group;
        const value = card.dataset.value;
        const multi = card.dataset.multi === 'true';

        if (multi) {
          card.classList.toggle('selected');
          if (!this.answers[group]) this.answers[group] = [];
          if (card.classList.contains('selected')) {
            if (!this.answers[group].includes(value)) this.answers[group].push(value);
          } else {
            this.answers[group] = this.answers[group].filter(v => v !== value);
          }
        } else {
          document.querySelectorAll(`[data-group="${group}"]`).forEach(c => c.classList.remove('selected'));
          card.classList.add('selected');
          this.answers[group] = value;
          // Auto-avance après 400ms
          setTimeout(() => this.nextQuestion(group), 400);
        }
      });
    });
  }

  /* ── Upload photo ── */
  bindPhotoUpload() {
    const input = document.getElementById('emmDiagPhoto');
    const zone  = document.getElementById('emmDiagUploadZone');
    if (!zone || !input) return;

    zone.addEventListener('click', () => input.click());
    zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('drag-over'); });
    zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
    zone.addEventListener('drop', e => {
      e.preventDefault();
      zone.classList.remove('drag-over');
      if (e.dataTransfer.files[0]) this.handlePhoto(e.dataTransfer.files[0]);
    });
    input.addEventListener('change', e => {
      if (e.target.files[0]) this.handlePhoto(e.target.files[0]);
    });

    // Skip photo
    document.getElementById('emmDiagSkipPhoto')?.addEventListener('click', () => this.showAnalyze());
  }

  handlePhoto(file) {
    if (!file.type.match('image.*')) return;
    const reader = new FileReader();
    reader.onload = e => {
      this.photo = e.target.result.split(',')[1];
      const preview = document.getElementById('emmDiagPhotoPreview');
      const zone = document.getElementById('emmDiagUploadZone');
      if (preview) { preview.src = e.target.result; preview.style.display = 'block'; }
      if (zone) zone.classList.add('has-photo');
      // Auto-avance après upload
      setTimeout(() => this.showAnalyze(), 800);
    };
    reader.readAsDataURL(file);
  }

  /* ── Navigation entre questions ── */
  bindNavigation() {
    document.querySelectorAll('.emm-diag-next').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.dataset.group;
        this.nextQuestion(group);
      });
    });
    document.querySelectorAll('.emm-diag-back').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.dataset.group;
        this.prevQuestion(group);
      });
    });
    document.getElementById('emmDiagAnalyze')?.addEventListener('click', () => this.showAnalyze());
  }

  /* ── Flux de questions ── */
  getFlow() {
    if (this.path === 'peau')   return ['priorite', 'type', 'photo'];
    if (this.path === 'moment') return ['moment', 'detail'];
    return ['priorite', 'moment', 'type', 'photo'];
  }

  nextQuestion(group) {
    const flow = this.getFlow();
    const idx  = flow.indexOf(group);
    if (idx >= 0 && idx < flow.length - 1) {
      this.showQuestion(flow[idx + 1]);
    } else {
      this.showAnalyze();
    }
  }

  prevQuestion(group) {
    const flow = this.getFlow();
    const idx  = flow.indexOf(group);
    if (idx > 0) this.showQuestion(flow[idx - 1]);
    else { this.hideAllPaths(); this.showStep('emmDiagStep0'); }
  }

  showQuestion(group) {
    document.querySelectorAll('.emm-diag-question').forEach(q => q.classList.remove('active'));
    const target = document.getElementById(`emmDiagQ-${group}`);
    if (target) {
      target.classList.add('active');
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    // Adapter question 'detail' selon moment choisi
    if (group === 'detail') this.adaptDetailQuestion();
  }

  adaptDetailQuestion() {
    const moment = this.answers['moment'];
    document.querySelectorAll('[data-moment-detail]').forEach(el => {
      el.style.display = 'none';
    });
    const target = document.querySelector(`[data-moment-detail="${moment}"]`);
    if (target) target.style.display = 'block';
  }

  /* ── Analyse ── */
  async showAnalyze() {
    this.hideAllPaths();
    const loading = document.getElementById('emmDiagLoading');
    if (loading) loading.style.display = 'flex';
    this.animateLoadingText();

    try {
      const result = this.photo
        ? await this.getAIRecommendations()
        : this.getLocalRecommendations();

      clearInterval(this.loadingInterval);
      if (loading) loading.style.display = 'none';
      this.showResults(result);
      this.saveProfile(result);
    } catch(e) {
      clearInterval(this.loadingInterval);
      if (loading) loading.style.display = 'none';
      this.showResults(this.getLocalRecommendations());
    }
  }

  animateLoadingText() {
    const texts = [
      'On analyse tes besoins...',
      'On cherche tes soins idéaux...',
      'On prépare ta routine...',
      'Presque prêt...'
    ];
    let i = 0;
    const el = document.getElementById('emmLoadingText');
    if (el) el.textContent = texts[0];
    this.loadingInterval = setInterval(() => {
      i = (i + 1) % texts.length;
      if (el) el.textContent = texts[i];
    }, 2000);
  }

  async getAIRecommendations() {
    const proxyUrl = (window.EmmeraDiagConfig || {}).proxyUrl;
    if (!proxyUrl) return this.getLocalRecommendations();
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: this.photo, answers: this.answers, path: this.path })
    });
    if (!response.ok) return this.getLocalRecommendations();
    return response.json();
  }

  getLocalRecommendations() {
    const priorite = Array.isArray(this.answers.priorite)
      ? this.answers.priorite[0]
      : this.answers.priorite;
    const moment = this.answers.moment;
    const type   = this.answers.type;

    const profiles = {
      seche: {
        titre: 'Une peau qui a besoin de douceur et d\'hydratation',
        message: 'Ta peau te réclame de l\'attention. Elle a besoin d\'être nourrie, réconfortée, enveloppée. On a exactement ce qu\'il lui faut.',
        routine_matin: ['Nettoyant doux & respectueux', 'Sérum hydratant à l\'acide hyaluronique', 'Crème nourrissante éclat'],
        routine_soir:  ['Eau micellaire douce', 'Sérum réparateur nuit', 'Baume nourrissant intense'],
        tags: ['hydratation', 'seche', 'toutes-phases']
      },
      imperfections: {
        titre: 'Une peau qui régule et se purifie',
        message: 'Les imperfections parlent de ton corps, de ton cycle, de ton rythme. Nos soins t\'aident à les accompagner sans agresser.',
        routine_matin: ['Nettoyant purifiant doux', 'Sérum régulateur niacinamide', 'Crème légère matifiante'],
        routine_soir:  ['Nettoyant double texture', 'Sérum anti-imperfections', 'Gel crème équilibrant'],
        tags: ['imperfections', 'regulation', 'purification']
      },
      eclat: {
        titre: 'Une peau qui cherche sa lumière',
        message: 'Le teint terne, c\'est souvent un manque d\'éclat plutôt qu\'un manque de soin. On va redonner à ta peau ce rayonnement naturel.',
        routine_matin: ['Nettoyant éclat vitamine C', 'Sérum lumière actifs botaniques', 'Crème protectrice éclat'],
        routine_soir:  ['Démaquillant doux', 'Masque éclat hebdomadaire', 'Sérum nuit régénérant'],
        tags: ['eclat', 'luminosite']
      },
      sensible: {
        titre: 'Une peau sensible qui mérite une attention particulière',
        message: 'Ta peau est réactive, et c\'est normal. Elle communique. Nos formules apaisantes sont pensées pour les peaux qui ont besoin de douceur.',
        routine_matin: ['Nettoyant ultra-doux sans parfum', 'Sérum apaisant calmant', 'Crème barrière protectrice'],
        routine_soir:  ['Eau micellaire sans alcool', 'Sérum réparateur sensitive', 'Crème apaisante nuit'],
        tags: ['sensible', 'apaisant']
      },
      antiage: {
        titre: 'Une peau mature qui mérite le meilleur',
        message: 'Ta peau a une histoire. Elle a traversé des cycles, des émotions, des transformations. Nos soins anti-âge travaillent avec elle, jamais contre elle.',
        routine_matin: ['Nettoyant régénérant', 'Sérum fermeté bakuchiol', 'Crème anti-âge nourrissante'],
        routine_soir:  ['Huile démaquillante', 'Sérum rétinol végétal', 'Crème nuit lifting'],
        tags: ['anti-age', 'fermete']
      },
      cernes: {
        titre: 'Un regard qui a besoin de repos',
        message: 'Le manque de luminosité autour des yeux est souvent lié au cycle et au manque de sommeil. On a des soins ciblés pour retrouver ton éclat.',
        routine_matin: ['Nettoyant doux', 'Sérum yeux dépigmentant', 'Crème hydratante anti-fatigue'],
        routine_soir:  ['Démaquillant doux yeux', 'Contour yeux régénérant', 'Baume récupérateur'],
        tags: ['cernes', 'regard', 'anti-fatigue']
      },
      cycle: {
        titre: 'Une peau connectée à ton cycle',
        message: 'Ton cycle influence tout — ta peau, ton humeur, ton énergie. Nos soins cycliques s\'adaptent à chaque phase pour être là au bon moment.',
        routine_matin: ['Soin selon ta phase du cycle', 'Sérum adaptogène', 'Protection légère SPF'],
        routine_soir:  ['Nettoyant doux', 'Sérum de phase', 'Crème nuit cyclique'],
        tags: ['cycle', 'toutes-phases']
      },
      menopause: {
        titre: 'Une peau en transition, accompagnée avec soin',
        message: 'La ménopause change la peau. Ce n\'est pas une dégradation — c\'est une transformation. Nos formules sont pensées pour cette étape précise.',
        routine_matin: ['Nettoyant surgras doux', 'Sérum fermeté éstrogènes-like', 'Crème riche anti-âge'],
        routine_soir:  ['Huile nettoyante', 'Sérum nuit régénérant', 'Baume nourrissant'],
        tags: ['menopause', 'fermete', 'hydratation']
      },
      maternite: {
        titre: 'Une peau qui vit un moment exceptionnel',
        message: 'Grossesse, post-partum, allaitement... Ton corps fait quelque chose d\'extraordinaire. Nos soins sont sûrs, doux, pensés pour cette période unique.',
        routine_matin: ['Nettoyant certifié safe grossesse', 'Sérum hydratant tolérance', 'Crème protectrice douce'],
        routine_soir:  ['Huile sèche vergetures', 'Sérum nuit douceur', 'Baume réparateur sans perturbateurs'],
        tags: ['maternite', 'douceur', 'safe-grossesse']
      }
    };

    const key = priorite || moment || 'seche';
    return profiles[key] || profiles['seche'];
  }

  /* ── Affichage résultats ── */
  showResults(result) {
    const el = document.getElementById('emmDiagResults');
    if (!el) return;
    el.style.display = 'block';
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const titre   = document.getElementById('emmResultTitre');
    const message = document.getElementById('emmResultMessage');
    if (titre)   titre.textContent   = result.titre   || '';
    if (message) message.textContent = result.message || '';

    this.renderRoutine(result);
    this.loadProducts(result.tags || ['toutes-phases']);
    this.bindAddAllToCart();
  }

  renderRoutine(result) {
    const matinEl = document.getElementById('emmRoutineMatin');
    const soirEl  = document.getElementById('emmRoutineSoir');
    const matin   = result.routine_matin || ['Nettoyant', 'Sérum', 'Crème'];
    const soir    = result.routine_soir  || ['Démaquillant', 'Sérum nuit', 'Soin réparateur'];

    if (matinEl) matinEl.innerHTML = matin.map((step, i) => `
      <div class="emm-routine-step">
        <span class="emm-routine-num">0${i+1}</span>
        <span class="emm-routine-step-name">${step}</span>
      </div>`).join('');

    if (soirEl) soirEl.innerHTML = soir.map((step, i) => `
      <div class="emm-routine-step">
        <span class="emm-routine-num">0${i+1}</span>
        <span class="emm-routine-step-name">${step}</span>
      </div>`).join('');
  }

  async loadProducts(tags) {
    const container = document.getElementById('emmResultProducts');
    if (!container) return;
    container.innerHTML = '<p style="font-family:\'Poppins\',sans-serif;font-size:.9rem;color:#9AA89B;text-align:center;padding:24px;">Chargement des soins recommandés…</p>';
    try {
      const response = await fetch('/collections/all/products.json?limit=4');
      const data = await response.json();
      this.renderProducts(data.products.slice(0, 4));
    } catch(e) {
      container.innerHTML = '<p style="font-family:\'Poppins\',sans-serif;font-size:.9rem;color:#9AA89B;text-align:center;padding:24px;">Découvre tous nos soins sur la boutique.</p>';
    }
  }

  renderProducts(products) {
    const container = document.getElementById('emmResultProducts');
    if (!container || !products.length) return;
    container.innerHTML = products.map(p => `
      <div class="emm-result-product">
        ${p.images[0] ? `<img src="${p.images[0].src}" alt="${p.title}" loading="lazy" width="200" height="250">` : '<div class="emm-result-product-img-ph"></div>'}
        <div class="emm-result-product-info">
          <p class="emm-result-product-tag">Recommandé pour toi</p>
          <h3 class="emm-result-product-name">${p.title}</h3>
          <p class="emm-result-product-price">${parseFloat(p.variants[0]?.price || 0).toFixed(2)} €</p>
          <button class="emm-btn-primary emm-add-cart" data-variant="${p.variants[0]?.id}" type="button">
            Ajouter au panier
          </button>
        </div>
      </div>`).join('');

    container.querySelectorAll('.emm-add-cart').forEach(btn => {
      btn.addEventListener('click', async e => {
        const id = e.currentTarget.dataset.variant;
        btn.disabled = true;
        btn.textContent = '...';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
          });
          btn.textContent = 'Ajouté !';
          btn.classList.add('added');
        } catch(e) {
          btn.textContent = 'Erreur';
          btn.disabled = false;
        }
      });
    });
  }

  bindAddAllToCart() {
    document.querySelectorAll('.emm-add-all-cart').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variants = [...document.querySelectorAll('.emm-add-cart[data-variant]')]
          .map(b => ({ id: Number(b.dataset.variant), quantity: 1 }));
        if (!variants.length) return;
        btn.textContent = '...';
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: variants })
        });
        window.location.href = '/cart';
      });
    });
  }

  /* ── Sauvegarde ── */
  saveProfile(result) {
    try {
      localStorage.setItem('emmera_diagnostic_v2', JSON.stringify({
        date: new Date().toLocaleDateString('fr-FR'),
        path: this.path,
        answers: this.answers,
        result: result
      }));
    } catch(e) {}
  }

  /* ── Helpers ── */
  showStep(id) {
    const el = document.getElementById(id);
    if (el) { el.style.display = 'block'; el.classList.add('emm-fade-in-up'); }
  }
  hideStep(id) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  hideAllPaths() {
    ['emmDiagPathPeau','emmDiagPathMoment','emmDiagPathComplet'].forEach(id => this.hideStep(id));
    document.querySelectorAll('.emm-diag-question').forEach(q => q.classList.remove('active'));
  }
  scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

window.EmmeraDiagConfig = {
  proxyUrl: 'https://dulcet-kitsune-eff94e.netlify.app/.netlify/functions/diagnostic'
};

document.addEventListener('DOMContentLoaded', () => { new EmmeraDiagnosticV2(); });
